// Robust helpers for reading backend responses (Blob/JSON) and extracting error messages.
// Every helper settles its promise - a failing read or parse never leaves the caller pending.

const MAX_TEXT_IN_MESSAGE = 200

// iResult -1 is reserved: layouts/default.vue treats it as session/data error (auto login + route to '/').
// Transport failures therefore use their HTTP status or TRANSPORT_ERROR so the user keeps the formular
// and gets an error dialog instead.
export const TRANSPORT_ERROR = -2

/**
 * Reads a Blob as text. Resolves with the text, rejects on a FileReader error.
 * Non-Blob values are returned as string (or '' for null/undefined) without FileReader.
 */
export function readBlobAsText(blob, FileReaderImpl) {
  return new Promise((resolve, reject) => {
    if (blob === null || blob === undefined) return resolve('')
    if (typeof blob === 'string') return resolve(blob)
    if (typeof Blob !== 'undefined' && !(blob instanceof Blob)) return resolve(typeof blob === 'object' ? JSON.stringify(blob) : String(blob))
    const ReaderImpl = FileReaderImpl || globalThis.FileReader
    if (!ReaderImpl && typeof blob.text === 'function') return blob.text().then(resolve, reject)
    const reader = new ReaderImpl()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error || new Error('FileReader error'))
    reader.onabort = () => reject(new Error('FileReader aborted'))
    try {
      reader.readAsText(blob)
    } catch (e) {
      reject(e)
    }
  })
}

/**
 * Parses backend response text as JSON. Throws an Error with a readable message if the
 * text is empty or no JSON (e.g. HTML error page of a proxy).
 */
export function parseBackendJSON(text, status) {
  const prefix = status ? `HTTP ${status}: ` : ''
  if (text === undefined || text === null || String(text).trim() === '') {
    throw new Error(prefix + 'Leere Serverantwort')
  }
  try {
    return JSON.parse(text)
  } catch (e) {
    const snippet = String(text).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, MAX_TEXT_IN_MESSAGE)
    throw new Error(prefix + 'Ungültige Serverantwort' + (snippet ? ' (' + snippet + ')' : ''))
  }
}

/**
 * Normalizes any axios/transport error to a backend-like result object {iResult, sErrorMsg}.
 * Never throws; tolerates errors without `response` (network abort, CORS, timeout).
 */
export async function errorToBackendResult(error, FileReaderImpl) {
  const response = error && error.response
  if (!response) {
    return {iResult: TRANSPORT_ERROR, sErrorMsg: (error && error.message) ? 'Keine Verbindung zum Server: ' + error.message : 'Keine Verbindung zum Server'}
  }
  try {
    const text = await readBlobAsText(response.data, FileReaderImpl)
    const resp = parseBackendJSON(text, response.status)
    if (resp && typeof resp === 'object') {
      // keep a backend iResult (incl. -1 session errors); only fill a missing/zero code
      if (resp.iResult === undefined || resp.iResult === null || resp.iResult === 0) resp.iResult = response.status || TRANSPORT_ERROR
      if (!resp.sErrorMsg) resp.sErrorMsg = response.status ? 'HTTP ' + response.status : 'Serverfehler'
      return resp
    }
    return {iResult: response.status || TRANSPORT_ERROR, sErrorMsg: String(resp)}
  } catch (e) {
    return {iResult: response.status || TRANSPORT_ERROR, sErrorMsg: e.message}
  }
}

/** Short text for console logging of an axios error; never throws. */
export function describeError(error) {
  const data = error && error.response && error.response.data
  if (data && typeof data === 'object' && !(typeof Blob !== 'undefined' && data instanceof Blob)) {
    return (data.sErrorMsg || '') + ', iResult: ' + data.iResult
  }
  if (error && error.response && error.response.status) return 'HTTP ' + error.response.status
  return (error && error.message) || String(error)
}
